// ============================================
// FallbackImg - Image component with React-controlled error fallback
// ============================================
// Solves the infinite request loop where:
// 1. A broken image URL triggers onError
// 2. onError sets src via DOM manipulation
// 3. React re-render resets src back to the broken URL
// 4. onError fires again → infinite loop
//
// This component tracks error state in React state,
// so re-renders always render the correct src.
// ============================================

import React, { useState, useCallback } from 'react';
import { getRestaurantConfigSync } from '../../config/restaurantConfig';

// Use the restaurant's primary logo as the default fallback image
const getDefaultFallback = () => {
  try {
    const sessionLogo = sessionStorage.getItem('branding_primary_logo');
    if (sessionLogo) return sessionLogo;

    const config = getRestaurantConfigSync();
    return config?.logo || '/logo_transparent.png';
  } catch {
    return '/logo_transparent.png';
  }
};

const FallbackImg = ({ src, fallback, alt, onError: externalOnError, ...props }) => {
  const resolvedFallback = fallback || getDefaultFallback();
  const [hasError, setHasError] = useState(!src);

  // Reset error state when src prop changes to a new valid value
  const [prevSrc, setPrevSrc] = useState(src);
  if (src !== prevSrc) {
    setPrevSrc(src);
    if (src) {
      setHasError(false);
    }
  }

  const handleError = useCallback(() => {
    setHasError(true);
    if (externalOnError) {
      externalOnError();
    }
  }, [externalOnError]);

  const imgSrc = hasError ? resolvedFallback : (src || resolvedFallback);

  return (
    <img
      src={imgSrc}
      alt={alt || ''}
      onError={handleError}
      {...props}
    />
  );
};

export default React.memo(FallbackImg);
